// Phase 1 (ADR-0024) admin list for adventures.
//
// Shape:
//   - Team + status filters
//   - Table of adventures with name, dates, status, commitment count
//   - "New adventure" CTA routes to /admin/adventures/new
//   - Each row links to /admin/adventures/:id
//
// Phase 2-3 will add Start/End buttons in the detail page. The
// list page intentionally has no destructive actions — all
// lifecycle transitions go through the detail page so the audit
// trail records a focused, single-page action.

import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../../lib/api';
import { adventuresApi } from './api';
import type { PredictabilityPoint, AdventureConfigSettings } from './api';
import type { Adventure, AdventureStatus } from './types';

const STATUS_OPTIONS: AdventureStatus[] = ['draft', 'active', 'closed', 'archived'];

function StatusBadge({ status }: { status: AdventureStatus }) {
  // Status colours match the existing admin badge palette so
  // operators don't have to learn a new visual vocabulary.
  const palette: Record<AdventureStatus, string> = {
    draft: 'bg-slate-700/40 text-slate-300 border-slate-600',
    active: 'bg-emerald-700/40 text-emerald-200 border-emerald-600',
    closed: 'bg-amber-700/40 text-amber-200 border-amber-600',
    archived: 'bg-zinc-700/40 text-zinc-400 border-zinc-600',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded border ${palette[status]}`}>
      {status}
    </span>
  );
}

function formatDate(iso: string): string {
  // The adventure starts_at / ends_at are ISO UTC strings; show
  // them in the operator's local timezone as YYYY-MM-DD HH:mm.
  // Use Intl.DateTimeFormat so the browser's locale drives the
  // hour format. Phase 1 lists a coarse date — finer formatting
  // arrives with the wall TV (Phase 5).
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Phase 4: simple SVG predictability trend. Renders each closed
// adventure as a bar (predictability % over time, oldest → newest
// left → right). Custom SVG, no chart library (matches the
// skill-tree decision: hand-rolled SVG over d3). A dashed line
// marks the 80% 'on target' threshold so the operator can see at
// a glance which sprints cleared the reward bar.
function PredictabilityCurve({ points }: { points: PredictabilityPoint[] }) {
  if (points.length === 0) {
    return (
      <div className="text-sm text-ink-muted py-6 text-center">
        No closed adventures yet — predictability trend appears after the first End.
      </div>
    );
  }
  const W = 560, H = 160, PAD = 8;
  const bars = points.slice(-16);  // last 16 sprints
  const slot = (W - PAD * 2) / bars.length;
  const barW = Math.min(26, slot * 0.7);
  const maxPct = 100;
  const threshY = H - PAD - (80 / maxPct) * (H - PAD * 2);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Predictability trend">
      {/* 80% threshold line */}
      <line x1={PAD} x2={W - PAD} y1={threshY} y2={threshY} stroke="#6b7280" strokeDasharray="3 3" strokeWidth="1" />
      <text x={W - PAD} y={threshY - 3} textAnchor="end" fontSize="8" fill="#6b7280">80%</text>
      {bars.map((p, i) => {
        const pct = p.predictability_pct ?? 0;
        const barH = (pct / maxPct) * (H - PAD * 2);
        const x = PAD + i * slot + (slot - barW) / 2;
        const y = H - PAD - barH;
        const color = pct >= 80 ? '#34d399' : pct >= 50 ? '#fbbf24' : '#f87171';
        return (
          <g key={p.id}>
            <rect x={x} y={y} width={barW} height={barH} fill={color} rx="2">
              <title>{`${p.name}: ${pct}% (${p.completed_count}/${p.committed_count})`}</title>
            </rect>
          </g>
        );
      })}
      <text x={PAD} y={H - 3} fontSize="8" fill="#6b7280">oldest</text>
      <text x={W - PAD} y={H - 3} textAnchor="end" fontSize="8" fill="#6b7280">recent</text>
    </svg>
  );
}

// ---- Phase 7: per-team scoring knobs editor ----
//
// Shown when a specific team is selected in the filter. Lets the
// operator tune the predictability threshold, per-tier coin
// amounts, default reward pool size, and terminal status names
// without a deploy. Falls back to defaults if the team has none.

function ConfigEditor({ teamId }: { teamId: string }) {
  const qc = useQueryClient();
  const { data, isPending, isError } = useQuery({
    queryKey: ['adventure-config', teamId],
    queryFn: () => adventuresApi.getConfig(teamId),
    enabled: !!teamId,
  });
  const [settings, setSettings] = useState<AdventureConfigSettings | null>(null);

  const save = useMutation({
    mutationFn: (patch: Partial<AdventureConfigSettings>) => adventuresApi.saveConfig(teamId, patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['adventure-config', teamId] }),
  });

  if (isPending) return <div className="text-sm text-ink-muted">Loading settings…</div>;
  if (isError || !data) return <div className="text-sm text-red-400">Failed to load settings.</div>;
  const current = settings ?? data.settings;
  const num = (k: keyof AdventureConfigSettings) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setSettings({ ...current, [k]: Number(e.target.value) });
  const statusText = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSettings({ ...current, terminal_status_names: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) });

  return (
    <div className="mb-4 bg-bg-elev border border-border rounded p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-ink">Scoring settings</h3>
        <button
          onClick={() => save.mutate(current)}
          disabled={save.isPending}
          className="px-3 py-1 bg-brand text-white text-sm rounded hover:bg-brand-dark disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save settings'}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs text-ink-muted">Predictability threshold (%)</span>
          <input type="number" value={current.predictable_threshold} onChange={num('predictable_threshold')} className="mt-1 w-full px-2 py-1 bg-bg-base border border-border rounded" />
        </label>
        <label className="block">
          <span className="text-xs text-ink-muted">Coins · predictable</span>
          <input type="number" value={current.coins_predictable} onChange={num('coins_predictable')} className="mt-1 w-full px-2 py-1 bg-bg-base border border-border rounded" />
        </label>
        <label className="block">
          <span className="text-xs text-ink-muted">Coins · goals hit</span>
          <input type="number" value={current.coins_goals_hit} onChange={num('coins_goals_hit')} className="mt-1 w-full px-2 py-1 bg-bg-base border border-border rounded" />
        </label>
        <label className="block">
          <span className="text-xs text-ink-muted">Coins · on target</span>
          <input type="number" value={current.coins_on_target} onChange={num('coins_on_target')} className="mt-1 w-full px-2 py-1 bg-bg-base border border-border rounded" />
        </label>
        <label className="block">
          <span className="text-xs text-ink-muted">Default reward pool size</span>
          <input type="number" value={current.default_reward_pool_size} onChange={num('default_reward_pool_size')} className="mt-1 w-full px-2 py-1 bg-bg-base border border-border rounded" />
        </label>
        <label className="block md:col-span-2">
          <span className="text-xs text-ink-muted">Terminal status names (comma-separated)</span>
          <input value={current.terminal_status_names.join(', ')} onChange={statusText} className="mt-1 w-full px-2 py-1 bg-bg-base border border-border rounded font-mono" />
        </label>
      </div>
    </div>
  );
}

export function AdventuresListPage() {
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: { id: string; name: string }[] }>('/api/teams'),
  });

  const [filterTeam, setFilterTeam] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  const adventures = useQuery({
    queryKey: ['admin-adventures', filterTeam, filterStatus],
    queryFn: () => adventuresApi.list({
      team_id: filterTeam || undefined,
      status: (filterStatus || undefined) as AdventureStatus | undefined,
    }),
  });

  // Phase 4: predictability trend. Closed adventures per team,
  // drives the SVG curve below.
  const history = useQuery({
    queryKey: ['admin-adventures-history', filterTeam],
    queryFn: () => adventuresApi.predictabilityHistory(filterTeam || undefined),
  });

  // Active-overview strip: count of active adventures + latest
  // live predictability for the filtered team (or across teams).
  const activeAdventures = (adventures.data?.adventures ?? []).filter((a) => a.status === 'active');

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Adventures</h1>
          <p className="text-sm text-ink-muted mt-1">
            Sprints as adventures. Author a draft, snapshot the commitment
            on Start, automate the predictability scoring on End.
          </p>
        </div>
        <Link
          to="/admin/adventures/new"
          className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark transition"
        >
          New adventure
        </Link>
      </div>

      <div className="flex gap-3 mb-4">
        <select
          value={filterTeam}
          onChange={(e) => setFilterTeam(e.target.value)}
          className="px-3 py-2 bg-bg-elev border border-border rounded text-sm"
        >
          <option value="">All teams</option>
          {(teams.data?.teams ?? []).map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-bg-elev border border-border rounded text-sm"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {filterTeam && <ConfigEditor teamId={filterTeam} />}

      {activeAdventures.length > 0 && (
        <div className="mb-4 px-4 py-3 bg-emerald-900/20 border border-emerald-700/40 rounded text-sm">
          <span className="text-emerald-200">
            <strong>{activeAdventures.length}</strong> active adventure{activeAdventures.length === 1 ? '' : 's'}
          </span>
          {activeAdventures.map((a) => (
            <Link key={a.id} to={`/admin/adventures/${a.id}`} className="ml-3 text-brand hover:underline">
              {a.name}
            </Link>
          ))}
        </div>
      )}

      {/* Phase 4: predictability trend for the filtered team */}
      <div className="mb-4 bg-bg-elev border border-border rounded p-4">
        <h3 className="text-sm font-medium text-ink mb-2">Predictability trend</h3>
        {history.isPending ? (
          <div className="text-sm text-ink-muted py-6 text-center">Loading…</div>
        ) : history.isError ? (
          <div className="text-sm text-red-400 py-6 text-center">Failed to load trend.</div>
        ) : (
          <PredictabilityCurve points={history.data?.points ?? []} />
        )}
      </div>

      <div className="bg-bg-elev border border-border rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-base text-ink-muted text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Team</th>
              <th className="text-left px-4 py-3">Window</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Commitment</th>
            </tr>
          </thead>
          <tbody>
            {adventures.isPending && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-muted">Loading…</td></tr>
            )}
            {adventures.isError && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-red-400">
                Failed to load adventures. {(adventures.error as Error).message}
              </td></tr>
            )}
            {adventures.data?.adventures.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-muted">
                No adventures yet. Click "New adventure" to author the first one.
              </td></tr>
            )}
            {adventures.data?.adventures.map((adv: Adventure) => (
              <tr key={adv.id} className="border-t border-border hover:bg-bg-base/40">
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/adventures/${adv.id}`}
                    className="text-brand hover:underline"
                  >
                    {adv.name}
                  </Link>
                  {adv.jira_sprint_id && (
                    <span className="ml-2 text-xs text-ink-muted font-mono">
                      {adv.jira_sprint_id}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-ink-muted">
                  {(teams.data?.teams ?? []).find((t) => t.id === adv.team_id)?.name ?? adv.team_id}
                </td>
                <td className="px-4 py-3 text-ink-muted text-xs">
                  {formatDate(adv.starts_at)} → {formatDate(adv.ends_at)}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={adv.status} />
                </td>
                <td className="px-4 py-3 text-right text-ink-muted">
                  {adv.commitment.length} ticket{adv.commitment.length === 1 ? '' : 's'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
